import Button from '../../../../../../../../common/_custom/Button/Button';
import Input from '../../../../../../../../common/_custom/Input/Input';
import LoadingSpinner from '../../../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Modal from '../../../../../../../../common/_custom/Modal/Modal';
import { numberOnly } from '../../../../../../../../utils/utils';
import ConfirmationPopup from '../../../../../../../../common/ConfirmationPopup/ConfirmationPopup';
import {
    LINKEDIN_CLEAR_CONFIRM_COPY,
    LINKEDIN_REFRESH_COPY,
    PHONE_COUNTRY_PREFIX,
} from '../../../../../../../../common/ManagementProfile/managementProfileFields';
import { useEditDirectorDetailsModal } from './useEditDirectorDetailsModal';
import type { EditDirectorDetailsModalProps } from '../ManagementUserProfile.types';
import styles from './EditDirectorDetailsModal.module.scss';

const PHONE_MAX_LENGTH = 10;
const noop = () => {};

const ReadOnlyField = ({ label, value }: { label: string; value?: string }) => (
    <div className={styles.FieldGroup}>
        <label className={styles.FieldLabel}>{label}</label>
        <Input
            value={value ?? ''}
            onChange={noop}
            showFloatingLabel={false}
            allowAllCharacters
            disabled
            containerClassName={styles.FieldContainer}
            inputClassName={`${styles.FieldInput} ${styles.ReadOnlyInput}`}
        />
    </div>
);

function EditDirectorDetailsModal({ open, onClose, director, onSave, linkedinRefreshing = false }: EditDirectorDetailsModalProps) {
    const {
        values,
        errors,
        saving,
        isSaveDisabled,
        setField,
        handleSave,
        confirmingLinkedInClear,
        confirmLinkedInClear,
        dismissLinkedInClear,
    } = useEditDirectorDetailsModal({
        director,
        onSave,
        onClose,
        linkedinRefreshing,
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="flexible"
            persistent
            style={{ padding: 0 }}
            closeIconStyle={{ height: '0.875rem', width: '0.875rem', top: '1.6rem', right: '1.5rem' }}
        >
            <div className={styles.EditDirectorDetailsModal}>
                <div className={styles.Header}>
                    <div className={styles.HeadingGroup}>
                        <h3 className={styles.Heading}>Edit Details</h3>
                        <span className={styles.HeaderDivider} aria-hidden="true" />
                        <span className={styles.DirectorName}>{director?.name}</span>
                    </div>
                </div>

                <div className={styles.Body}>
                    <ReadOnlyField label="Name" value={director?.name} />

                    <div className={styles.FieldRow}>
                        <ReadOnlyField label="PAN" value={director?.pan} />
                        <ReadOnlyField label="DIN" value={director?.din} />
                    </div>

                    <div className={styles.FieldGroup}>
                        <label className={styles.FieldLabel}>Email</label>
                        <Input
                            placeholder="Add email"
                            value={values.email}
                            onChange={(value: string) => setField('email', value)}
                            showFloatingLabel={false}
                            allowAllCharacters
                            isValid={!errors.email}
                            errorMessage={errors.email}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.FieldInput}
                        />
                    </div>

                    <div className={styles.FieldGroup}>
                        <label className={styles.FieldLabel}>Phone No</label>
                        <div className={styles.PhoneRow}>
                            <span className={styles.CountryPrefix}>{PHONE_COUNTRY_PREFIX}</span>
                            <Input
                                placeholder="Add phone number"
                                value={values.phone}
                                onChange={(value: string) => setField('phone', numberOnly(value))}
                                showFloatingLabel={false}
                                maxLength={PHONE_MAX_LENGTH}
                                isValid={!errors.phone}
                                errorMessage={errors.phone}
                                containerClassName={styles.PhoneFieldContainer}
                                inputClassName={styles.FieldInput}
                            />
                        </div>
                    </div>

                    <div className={styles.FieldGroup}>
                        <div className={styles.FieldLabelRow}>
                            <label className={styles.FieldLabel}>LinkedIn</label>
                            {linkedinRefreshing && (
                                <span className={styles.FetchingLabel}>
                                    <LoadingSpinner height="0.75rem" color="var(--primary-purple)" />
                                    {LINKEDIN_REFRESH_COPY.FETCHING}
                                </span>
                            )}
                        </div>
                        <Input
                            placeholder="Add LinkedIn profile URL"
                            value={values.linkedinUrl}
                            onChange={(value: string) => setField('linkedinUrl', value)}
                            showFloatingLabel={false}
                            allowAllCharacters
                            isValid={!errors.linkedinUrl}
                            errorMessage={errors.linkedinUrl}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.FieldInput}
                        />
                        {linkedinRefreshing && <span className={styles.FetchingHint}>{LINKEDIN_REFRESH_COPY.STALE_HINT}</span>}
                    </div>

                    <div className={styles.FieldGroup}>
                        <label className={styles.FieldLabel}>Address</label>
                        <Input
                            placeholder="Add address"
                            value={values.address}
                            onChange={(value: string) => setField('address', value)}
                            showFloatingLabel={false}
                            allowAllCharacters
                            isValid={!errors.address}
                            errorMessage={errors.address}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.FieldInput}
                        />
                    </div>

                    <div className={styles.FieldGroup}>
                        <label className={styles.FieldLabel}>Overview</label>
                        <Input
                            type="textarea"
                            placeholder="Add overview"
                            value={values.managementProfileSummary}
                            onChange={(value: string) => setField('managementProfileSummary', value)}
                            showFloatingLabel={false}
                            allowAllCharacters
                            textAreaRows={5}
                            isValid={!errors.managementProfileSummary}
                            errorMessage={errors.managementProfileSummary}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.OverviewInput}
                        />
                    </div>
                </div>

                {confirmingLinkedInClear && (
                    <ConfirmationPopup
                        open
                        title={LINKEDIN_CLEAR_CONFIRM_COPY.TITLE}
                        subTitle={LINKEDIN_CLEAR_CONFIRM_COPY.SUBTITLE}
                        confirmCta={LINKEDIN_CLEAR_CONFIRM_COPY.CONFIRM}
                        cancelCta={LINKEDIN_CLEAR_CONFIRM_COPY.CANCEL}
                        onConfirm={confirmLinkedInClear}
                        onCancel={dismissLinkedInClear}
                    />
                )}

                <div className={styles.Footer}>
                    <Button
                        text="Save"
                        variant="primary"
                        size="wide"
                        onClick={handleSave}
                        loading={saving}
                        disabled={isSaveDisabled}
                        className={styles.SaveButton}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default EditDirectorDetailsModal;
